/**
 * General Settings Store (Zustand)
 * Manages state for DCS App configuration
 */

import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { CreateDcsAppRequest, DcsApp, GeneralSettingsState } from '../types/general-settings-types';

export const useGeneralSettingsStore = create<
  GeneralSettingsState & {
    fetchSettings: (operatorId?: number) => Promise<void>;
    createSettings: (request: CreateDcsAppRequest) => Promise<void>;
    updateSettings: (app: DcsApp) => Promise<void>;
    resetSettings: () => void;
    openDialog: () => Promise<void>;
    closeDialog: () => void;
    hasChanges: () => boolean;
    clearError: () => void;
  }
>((set, get) => ({
  app: null,
  originalApp: null,
  isLoading: false,
  isSaving: false,
  error: null,
  isDialogOpen: false,

  /**
   * Check if there are unsaved changes
   */
  hasChanges: () => {
    const { app, originalApp } = get();
    if (!app || !originalApp) return false;
    return JSON.stringify(app) !== JSON.stringify(originalApp);
  },

  /**
   * Fetch settings from API
   */
  fetchSettings: async (operatorId?: number) => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const app = await services.getGeneralSettingsUseCase.execute(operatorId);

      if (app) {
        set({
          app,
          originalApp: JSON.parse(JSON.stringify(app)) // Deep copy
        });
      }
      // If no app found, leave app as null — the page will handle this as create mode
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[GeneralSettingsStore] Failed to fetch settings:', errorMessage);
      set({ error: errorMessage });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Create a new DCS App (operator not yet registered as EV operator)
   */
  createSettings: async (request: CreateDcsAppRequest) => {
    set({ isSaving: true, error: null });
    try {
      const { generalSettingsRepository } = servicesContainer.getServices();
      await generalSettingsRepository.createSettings(request);
      // Re-fetch from API to get the full server-populated data (same as opening the page for an existing operator)
      await get().fetchSettings(request.operatorId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  /**
   * Update settings
   */
  updateSettings: async (app: DcsApp) => {
    set({ isSaving: true, error: null });
    try {
      const { originalApp } = get();

      if (!originalApp) {
        throw new Error('No original settings found');
      }

      const services = servicesContainer.getServices();
      await services.updateGeneralSettingsUseCase.execute(app, originalApp);

      // Update state after successful save
      set({
        app,
        originalApp: JSON.parse(JSON.stringify(app)) // Deep copy
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[GeneralSettingsStore] Failed to update settings:', errorMessage);
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  /**
   * Reset to original settings
   */
  resetSettings: () => {
    const { originalApp } = get();
    if (originalApp) {
      set({
        app: JSON.parse(JSON.stringify(originalApp))
      });
    }
  },

  /**
   * Open dialog and fetch settings
   */
  openDialog: async () => {
    set({ isDialogOpen: true });
    await get().fetchSettings();
  },

  /**
   * Close dialog
   */
  closeDialog: () => {
    set({ isDialogOpen: false });
  },

  clearError: () => {
    set({ error: null });
  }
}));
